import { useState, useEffect, useCallback } from 'react';
import { Modal, DefaultButton, PrimaryButton, Text, TextField, ComboBox, Spinner, SpinnerSize } from '@fluentui/react';
import { StarRegular, StarFilled } from '@fluentui/react-icons';
import {
  createLongListeportContainer,
  DocumentMetadata,
  LongListreportRequest,
  uploadTrafficLightReport,
  processLongListReport,
  generateCriteriaDescriptions,
} from '../../api';
import { toast } from 'react-toastify'; // Import toastify and ToastContainer
import { useAuthErrorContext } from '../AuthErrorProvider';
import { useIntl } from 'react-intl';
import { MAX_TRAFFIC_LIGHT_REPORTS } from '../../constants/reportLimits';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import styles from './TrafficLightModal.module.scss';

interface FormFields {
  roleTitle: string;
  roleOverview: string;
}

enum TrafficLightPhase {
  RoleDetails = 0,
  CandidateOrder = 1,
  CriteriaSetup = 2,
  Ratings = 3,
}

interface SortableCandidateProps {
  id: string;
  item: any;
}

const SortableCandidate: React.FC<SortableCandidateProps> = ({ id, item }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    transition: {
      duration: 0,
      easing: 'linear',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : 'transform 150ms cubic-bezier(0.25, 1, 0.5, 1)',
    padding: '1rem',
    marginBottom: '0.5rem',
    background: isDragging ? '#f0f0f0' : 'white',
    border: '1px solid #edebe9',
    borderRadius: '4px',
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    cursor: isDragging ? 'grabbing' : 'grab',
    position: 'relative' as const,
    zIndex: isDragging ? 1 : 0,
    boxShadow: isDragging ? '0 5px 15px rgba(0,0,0,0.15)' : 'none',
    touchAction: 'none',
  };

  const candidateData = item.details?.candidatereport_data || {};

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
        {item.details?.photo_data && (
          <img
            src={item.details.photo_data}
            alt={candidateData.candidateFirstName + ' ' + candidateData.candidateLastName}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
            draggable={false}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
          <Text variant="large" style={{ fontWeight: 600 }}>
            {candidateData.candidateFirstName + ' ' + candidateData.candidateLastName}
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Text variant="small" style={{ color: '#605e5c' }}>
              {candidateData.candidateTitle}{' '}
              {candidateData.candidateRecentEmployer ? `at ${candidateData.candidateRecentEmployer}` : ''}
            </Text>
          </div>
        </div>
      </div>
      <div
        style={{
          color: '#605e5c',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="2" cy="2" r="2" fill="currentColor" />
          <circle cx="2" cy="8" r="2" fill="currentColor" />
          <circle cx="2" cy="14" r="2" fill="currentColor" />
          <circle cx="2" cy="20" r="2" fill="currentColor" />
          <circle cx="8" cy="2" r="2" fill="currentColor" />
          <circle cx="8" cy="8" r="2" fill="currentColor" />
          <circle cx="8" cy="14" r="2" fill="currentColor" />
          <circle cx="8" cy="20" r="2" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
};

function TrafficLightModal({
  checkedItems,
  isTrafficLightModalOpen,
  setIsTrafficLightModalOpen,
  onClose,
}: {
  checkedItems: any[];
  isTrafficLightModalOpen: boolean;
  setIsTrafficLightModalOpen: (val: boolean) => void;
  onClose?: () => void;
}) {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [formValues, setFormValues] = useState(
    checkedItems.map(() => ({
      document: '',
      criteria1: '',
      criteria2: '',
      criteria3: '',
      criteria4: '',
      criteria5: '',
      rating1: 0,
      rating2: 0,
      rating3: 0,
      rating4: 0,
      rating5: 0,
      average1: 0.0,
      average2: 0.0,
      average3: 0.0,
      average4: 0.0,
      average5: 0.0,
      documentCount: 0,
      position: 0,
      description1: '',
      description2: '',
      description3: '',
      description4: '',
      description5: '',
    })),
  );
  const [criteriaLabels, setCriteriaLabels] = useState(['', '', '', '', '']);
  const [customCriteriaMode, setCustomCriteriaMode] = useState([false, false, false, false, false]);
  const [customCriteriaTexts, setCustomCriteriaTexts] = useState(['', '', '', '', '']);
  const [descriptions, setDescriptions] = useState(['', '', '', '', '']);
  const [phase, setPhase] = useState<TrafficLightPhase>(TrafficLightPhase.RoleDetails);
  const [isDescriptionEnabled, setIsDescriptionEnabled] = useState(false);
  const allCriteriaFilled = criteriaLabels.every(label => label !== '');
  const allStarsFilled = Object.values(formValues).length == checkedItems.length;
  const { handleAuthError } = useAuthErrorContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formFields, setFormFields] = useState<FormFields>({
    roleTitle:
      checkedItems[0]?.details?.candidatereport_data?.positionName ||
      checkedItems[0]?.details?.candidatereport_data?.roleTitle ||
      '',
    roleOverview: checkedItems[0]?.details?.candidatereport_data?.jobDescription || '',
  });
  const intl = useIntl();
  const [isGeneratingDescriptions, setIsGeneratingDescriptions] = useState(false);
  const [orderedCheckedItems, setOrderedCheckedItems] = useState([...checkedItems]);
  const [isConfirmingGeneration, setIsConfirmingGeneration] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = parseInt(active.id as string);
    const newIndex = parseInt(over.id as string);

    setOrderedCheckedItems(items => {
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const resetModalState = () => {
    setCurrentItemIndex(0);
    setFormValues(
      checkedItems.map(() => ({
        document: '',
        criteria1: '',
        criteria2: '',
        criteria3: '',
        criteria4: '',
        criteria5: '',
        rating1: 0,
        rating2: 0,
        rating3: 0,
        rating4: 0,
        rating5: 0,
        average1: 0.0,
        average2: 0.0,
        average3: 0.0,
        average4: 0.0,
        average5: 0.0,
        documentCount: 0,
        position: 0,
        description1: '',
        description2: '',
        description3: '',
        description4: '',
        description5: '',
      })),
    );
    setCriteriaLabels(['', '', '', '', '']);
    setCustomCriteriaMode([false, false, false, false, false]);
    setCustomCriteriaTexts(['', '', '', '', '']);
    setDescriptions(['', '', '', '', '']);
    setPhase(TrafficLightPhase.RoleDetails);
    setIsDescriptionEnabled(false);
    setIsSubmitting(false);
    setFormFields({
      roleTitle:
        checkedItems[0]?.details?.candidatereport_data?.positionName ||
        checkedItems[0]?.details?.candidatereport_data?.roleTitle ||
        '',
      roleOverview: checkedItems[0]?.details?.candidatereport_data?.jobDescription || '',
    });
    setIsGeneratingDescriptions(false);
    setOrderedCheckedItems([...checkedItems]);
    if (false) {
      //remove in future
      setIsConfirmingGeneration(false);
    }
  };

  const handleModalDismiss = () => {
    resetModalState();
    setIsTrafficLightModalOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const newRoleTitle =
      checkedItems[0]?.details?.candidatereport_data?.positionName ||
      checkedItems[0]?.details?.candidatereport_data?.roleTitle ||
      '';
    const newRoleOverview = checkedItems[0]?.details?.candidatereport_data?.jobDescription || '';

    if (newRoleTitle || newRoleOverview) {
      setFormFields(prev => ({
        ...prev,
        roleTitle: newRoleTitle,
        roleOverview: newRoleOverview,
      }));
    }
  }, [checkedItems]);

  useEffect(() => {
    if (isTrafficLightModalOpen) {
      setOrderedCheckedItems([...checkedItems]);
      setFormValues([]);
    }
  }, [isTrafficLightModalOpen]);

  useEffect(() => {
    if (isTrafficLightModalOpen) {
      setOrderedCheckedItems([...checkedItems]);
      setFormValues(
        checkedItems.map(() => ({
          document: '',
          criteria1: '',
          criteria2: '',
          criteria3: '',
          criteria4: '',
          criteria5: '',
          rating1: 0,
          rating2: 0,
          rating3: 0,
          rating4: 0,
          rating5: 0,
          average1: 0.0,
          average2: 0.0,
          average3: 0.0,
          average4: 0.0,
          average5: 0.0,
          documentCount: 0,
          position: 0,
          description1: '',
          description2: '',
          description3: '',
          description4: '',
          description5: '',
        })),
      );
    }
  }, [isTrafficLightModalOpen, checkedItems]);

  const handleFormChange = (field: any, value: any) => {
    const newFormValues = [...formValues];
    (newFormValues as any)[currentItemIndex][field] = value;
    setFormValues(newFormValues);
  };

  const handleCriteriaSelection = (criteriaIndex: number, option?: string) => {
    if (!option) return;

    const newCustomMode = [...customCriteriaMode];
    const newCriteriaLabels = [...criteriaLabels];

    if (option === 'Other') {
      newCustomMode[criteriaIndex] = true;
      newCriteriaLabels[criteriaIndex] = '';
    } else {
      newCustomMode[criteriaIndex] = false;
      newCriteriaLabels[criteriaIndex] = option;
    }

    setCustomCriteriaMode(newCustomMode);
    setCriteriaLabels(newCriteriaLabels);
  };

  const handleCustomCriteriaChange = (index: number, value: string) => {
    const newCriteriaLabels = [...criteriaLabels];
    newCriteriaLabels[index] = value;
    setCriteriaLabels(newCriteriaLabels);
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const newDescriptions = [...descriptions];
    newDescriptions[index] = value;
    setDescriptions(newDescriptions);
  };

  const generateUniqueId = (): string => {
    return `templonglistreportcontainer-${Math.random().toString(36).substring(2, 15)}`;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const updatedFormValues = { ...formValues };

    let totalCriteria1 = 0.0;
    let totalCriteria2 = 0.0;
    let totalCriteria3 = 0.0;
    let totalCriteria4 = 0.0;
    let totalCriteria5 = 0.0;
    const numberOfDocuments = Object.keys(updatedFormValues).length;

    Object.keys(updatedFormValues).forEach(key => {
      const item = updatedFormValues[key as any];
      totalCriteria1 += item.rating1 || 1;
      totalCriteria2 += item.rating2 || 1;
      totalCriteria3 += item.rating3 || 1;
      totalCriteria4 += item.rating4 || 1;
      totalCriteria5 += item.rating5 || 1;
    });

    const average1 = totalCriteria1 / numberOfDocuments;
    const average2 = totalCriteria2 / numberOfDocuments;
    const average3 = totalCriteria3 / numberOfDocuments;
    const average4 = totalCriteria4 / numberOfDocuments;
    const average5 = totalCriteria5 / numberOfDocuments;
    const genBatchId = generateUniqueId();

    const updatedFormValuesArray: DocumentMetadata[] = Object.keys(updatedFormValues).map((key, index) => {
      const updatedItem: DocumentMetadata = {
        roleTitle: formFields.roleTitle,
        roleOverview: formFields.roleOverview,
        document: updatedFormValues[key as any].document,
        criteria1: criteriaLabels[0],
        criteria2: criteriaLabels[1],
        criteria3: criteriaLabels[2],
        criteria4: criteriaLabels[3],
        criteria5: criteriaLabels[4],
        rating1: updatedFormValues[key as any].rating1,
        rating2: updatedFormValues[key as any].rating2,
        rating3: updatedFormValues[key as any].rating3,
        rating4: updatedFormValues[key as any].rating4,
        rating5: updatedFormValues[key as any].rating5,
        average1: average1,
        average2: average2,
        average3: average3,
        average4: average4,
        average5: average5,
        batchId: genBatchId,
        description1: descriptions[0],
        description2: descriptions[1],
        description3: descriptions[2],
        description4: descriptions[3],
        description5: descriptions[4],
        documentCount: Object.keys(updatedFormValues).length,
        position: index + 1,
      };

      criteriaLabels.forEach((label, labelIndex) => {
        (updatedItem as any)[`criteria${labelIndex + 1}`] = label;
      });

      updatedItem.document = orderedCheckedItems[index]?.filename ?? '';
      return updatedItem;
    });

    const longListReportRequest: LongListreportRequest = {
      messages: updatedFormValuesArray,
    };

    try {
      const response = await createLongListeportContainer(genBatchId, handleAuthError);
      if (response.status === 200) {
      } else {
        console.error('Failed to post data:', response.status, response.error);
      }
    } catch (error) {
      console.error('Error posting data:', error);
    }

    try {
      const response = await uploadTrafficLightReport(longListReportRequest, handleAuthError);
      if (response.status === 200) {
        toast.success('Traffic light created successfully');
      } else {
        console.error('Failed to post data:', response.status, response.error);
      }
    } catch (error) {
      console.error('Error posting data:', error);
    }

    try {
      const response = await processLongListReport(genBatchId, handleAuthError);
      if (response.status === 200) {
      } else {
        toast.error('An error occurred while processing the report' + response.status);
      }
    } catch (error) {
      toast.error('An error occurred while processing the report');
    } finally {
      setFormValues([]);
      setIsSubmitting(false);
      resetModalState();
      setIsTrafficLightModalOpen(false);
      if (onClose) {
        onClose();
      }
    }
  };

  const handleGenerateDescriptions = async () => {
    try {
      if (false) {
        //remove in future
        if (isConfirmingGeneration) {
          setIsConfirmingGeneration(false);
        }
      }

      setIsGeneratingDescriptions(true);
      const filledCriteria = criteriaLabels.filter(label => label !== '');
      if (filledCriteria.length === 0) {
        toast.error('Please select at least one criteria first');
        return;
      }

      const response = await generateCriteriaDescriptions({ criteria: criteriaLabels }, handleAuthError);

      if (response.status === 500) {
        toast.error('Server error: Failed to generate descriptions. Please try again later.');
        return;
      }

      if (!response.data) {
        toast.error('Failed to generate descriptions: No data received');
        return;
      }

      setDescriptions(response.data.descriptions);
      setIsDescriptionEnabled(true);
      toast.success('Descriptions generated successfully');
    } catch (error: any) {
      const errorMessage =
        error?.status === 500
          ? 'Server error: Failed to generate descriptions. Please try again later.'
          : 'Failed to generate descriptions';

      toast.error(errorMessage);
      console.error('Error generating descriptions:', error);
    } finally {
      setIsGeneratingDescriptions(false);
    }
  };

  const modalContent = () => {
    switch (phase) {
      case TrafficLightPhase.RoleDetails:
        return (
          <form style={{ paddingTop: '1rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                {intl.formatMessage({ id: 'trafficLightModal.roleTitle.label' })}{' '}
                <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={formFields.roleTitle}
                onChange={e => setFormFields(prev => ({ ...prev, roleTitle: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                {intl.formatMessage({ id: 'trafficLightModal.roleOverview.label' })}{' '}
                <span style={{ color: 'red' }}>*</span>
              </label>
              <textarea
                value={formFields.roleOverview}
                onChange={e => setFormFields(prev => ({ ...prev, roleOverview: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  minHeight: '100px',
                }}
              />
            </div>
          </form>
        );
      case TrafficLightPhase.CandidateOrder:
        return (
          <div style={{ paddingTop: '1rem' }}>
            <Text block style={{ marginBottom: '2rem' }}>
              {intl.formatMessage({ id: 'trafficLightModal.candidateOrder.description' })}
            </Text>
            <div className={styles.candidatesContainer}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext
                  items={orderedCheckedItems.map((_, index) => index.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedCheckedItems.map((item, index) => (
                    <SortableCandidate key={index} id={index.toString()} item={item} />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        );
      case TrafficLightPhase.CriteriaSetup: {
        const predefinedCriteria = [
          'Academic credibility',
          'Analytical skills',
          'Change management',
          'Commercial',
          'Communication',
          'Content development',
          'Corporate culture',
          'Culture',
          'Deal making',
          'Development lifecycle',
          'Digital experience',
          'Digital proficiency & work practices',
          'Emotional Intelligence',
          'Executive leadership',
          'Executive presence',
          'Fundraising',
          'Governance',
          'Governance and process',
          'Government',
          'Industrial property',
          'Industry',
          'Investment',
          'Law qualifications',
          'Leadership 1',
          'Leadership 2',
          'Legal & risk',
          'Legal practice experience',
          'Machine learning AI',
          'Major projects',
          'Marketing & communications',
          'Non-for-profit experience',
          'Organisation',
          'Pace & pivot',
          'Partnerships & stakeholders',
        ];

        return (
          <>
            <form className={styles.criteriaForm}>
              {[1, 2, 3, 4, 5].map(criteriaIndex => (
                <div style={{ marginBottom: '1rem' }} key={criteriaIndex}>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>Criteria {criteriaIndex}:</label>
                  <ComboBox
                    selectedKey={
                      customCriteriaMode[criteriaIndex - 1] ? 'Other' : criteriaLabels[criteriaIndex - 1] || undefined
                    }
                    onChange={(_, option) => handleCriteriaSelection(criteriaIndex - 1, option?.key as string)}
                    placeholder={intl.formatMessage({ id: 'trafficLightModal.criteria.placeholder' })}
                    options={[
                      { key: '', text: intl.formatMessage({ id: 'trafficLightModal.criteria.selectOption' }) },
                      ...predefinedCriteria.map(criteria => ({
                        key: criteria,
                        text: criteria,
                      })),
                      { key: 'Other', text: 'Other' },
                    ]}
                    allowFreeform={true}
                    autoComplete="on"
                    styles={{
                      root: { width: '100%', marginBottom: '0.5rem' },
                      input: { width: '100%' },
                    }}
                  />
                  {customCriteriaMode[criteriaIndex - 1] && (
                    <TextField
                      value={criteriaLabels[criteriaIndex - 1]}
                      onChange={(_, value) => handleCustomCriteriaChange(criteriaIndex - 1, value || '')}
                      placeholder="Enter custom criteria"
                      styles={{ root: { marginBottom: '0.5rem' } }}
                    />
                  )}
                  {isDescriptionEnabled && (
                    <>
                      <label style={{ display: 'block', marginBottom: '0.5rem', marginTop: '0.5rem' }}>
                        Description:
                      </label>
                      <TextField
                        multiline
                        rows={3}
                        value={descriptions[criteriaIndex - 1]}
                        onChange={(_, value) => handleDescriptionChange(criteriaIndex - 1, value || '')}
                        styles={{ root: { width: '100%' } }}
                      />
                    </>
                  )}
                </div>
              ))}
            </form>
          </>
        );
      }
      case TrafficLightPhase.Ratings:
        return (
          <>
            <form style={{ paddingTop: '2rem' }}>
              {criteriaLabels.map((criteriaLabel, index) => (
                <div style={{ marginBottom: '2rem' }} key={index}>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                    {criteriaLabel} <span style={{ color: 'red' }}>*</span>
                  </label>
                  {isDescriptionEnabled && (
                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>{descriptions[index]}</label>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {[1, 2, 3, 4, 5].map(star => {
                      const currentForm = formValues[currentItemIndex] || {};
                      const currentRating = (currentForm as any)[`rating${index + 1}`] || 0;
                      return (
                        <span
                          key={star}
                          onClick={() => {
                            const updatedFormValues = [...formValues];
                            updatedFormValues[currentItemIndex] = {
                              ...updatedFormValues[currentItemIndex],
                              [`rating${index + 1}`]: star,
                            };
                            setFormValues(updatedFormValues);
                          }}
                          style={{ cursor: 'pointer', marginRight: '0.5rem' }}
                        >
                          {currentRating >= star ? (
                            <StarFilled style={{ color: '#EAA300', width: '28px', height: '28px' }} />
                          ) : (
                            <StarRegular style={{ width: '28px', height: '28px' }} />
                          )}
                        </span>
                      );
                    })}
                    <DefaultButton
                      onClick={() => {
                        const updatedFormValues = [...formValues];
                        updatedFormValues[currentItemIndex] = {
                          ...updatedFormValues[currentItemIndex],
                          [`rating${index + 1}`]: 0,
                        };
                        setFormValues(updatedFormValues);
                      }}
                      text="Clear"
                      style={{ marginLeft: 'auto', height: '24px' }}
                    />
                  </div>
                </div>
              ))}
            </form>
          </>
        );
    }
  };

  const areAllCandidatesRated = () => {
    if (formValues.length !== orderedCheckedItems.length) return false;

    // Check that we have a formValue entry for each candidate
    if (formValues.length < orderedCheckedItems.length) return false;

    // Check that all candidates have ratings for all selected criteria
    const activeLabelsCount = criteriaLabels.filter(label => label !== '').length;
    if (activeLabelsCount === 0) return false;

    for (let i = 0; i < formValues.length; i++) {
      for (let j = 0; j < activeLabelsCount; j++) {
        // Rating fields are 1-indexed in the data structure
        const ratingField = `rating${j + 1}`;
        // Check if this candidate has a rating > 0 for this criteria
        if (!(ratingField in formValues[i]) || (formValues[i] as any)[ratingField] <= 0) {
          return false;
        }
      }
    }

    return true;
  };

  // Check if we have exceeded the maximum number of reports at component init
  useEffect(() => {
    if (isTrafficLightModalOpen && checkedItems.length > MAX_TRAFFIC_LIGHT_REPORTS) {
      toast.error(
        intl.formatMessage(
          { id: 'trafficLightModal.error.tooManyReports' },
          { maxReports: MAX_TRAFFIC_LIGHT_REPORTS, selectedCount: checkedItems.length },
        ),
      );
      setIsTrafficLightModalOpen(false);
      if (onClose) {
        onClose();
      }
    }
  }, [isTrafficLightModalOpen, checkedItems.length]);

  return (
    <Modal isOpen={isTrafficLightModalOpen} onDismiss={handleModalDismiss} isBlocking={true}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem',
          borderBottom: '1px solid #ccc',
          gap: '1rem',
          minWidth: '600px',
          overflowY: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
          {phase === TrafficLightPhase.Ratings && orderedCheckedItems[currentItemIndex]?.details?.photo_data && (
            <img
              src={orderedCheckedItems[currentItemIndex]?.details.photo_data}
              alt={intl.formatMessage(
                { id: 'trafficLightModal.candidatePhoto.alt' },
                {
                  name:
                    orderedCheckedItems[currentItemIndex]?.details?.candidatereport_data?.candidateFirstName +
                    ' ' +
                    orderedCheckedItems[currentItemIndex]?.details?.candidatereport_data?.candidateLastName,
                },
              )}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          )}
          <span
            style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {phase === TrafficLightPhase.RoleDetails || phase === TrafficLightPhase.CriteriaSetup
              ? intl.formatMessage({ id: 'trafficLightModal.roleDetails.title' })
              : phase === TrafficLightPhase.CandidateOrder
                ? intl.formatMessage({ id: 'trafficLightModal.candidateOrder.title' })
                : orderedCheckedItems[currentItemIndex]?.details?.candidatereport_data?.candidateFirstName +
                  ' ' +
                  orderedCheckedItems[currentItemIndex]?.details?.candidatereport_data?.candidateLastName}
          </span>
        </div>
        <DefaultButton onClick={handleModalDismiss} iconProps={{ iconName: 'Cancel' }} />
      </div>
      <div className={styles.contentContainer}>{modalContent()}</div>
      <div className={styles.footerContainer}>
        <DefaultButton
          onClick={() => {
            switch (phase) {
              case TrafficLightPhase.CandidateOrder:
                setPhase(TrafficLightPhase.RoleDetails);
                break;
              case TrafficLightPhase.CriteriaSetup:
                setPhase(TrafficLightPhase.CandidateOrder);
                break;
              case TrafficLightPhase.Ratings:
                setPhase(TrafficLightPhase.CriteriaSetup);
                break;
            }
          }}
          text={intl.formatMessage({
            id:
              phase === TrafficLightPhase.CandidateOrder
                ? 'trafficLightModal.backToRole.button'
                : phase === TrafficLightPhase.CriteriaSetup
                  ? 'trafficLightModal.backToOrder.button'
                  : phase === TrafficLightPhase.Ratings
                    ? 'trafficLightModal.backToCriteria.button'
                    : 'trafficLightModal.backGeneric.button',
          })}
          disabled={phase === TrafficLightPhase.RoleDetails}
        />

        {phase === TrafficLightPhase.RoleDetails ? (
          <PrimaryButton
            onClick={() => setPhase(TrafficLightPhase.CandidateOrder)}
            text={intl.formatMessage({ id: 'trafficLightModal.next.button' })}
            disabled={!formFields.roleTitle || !formFields.roleOverview}
          />
        ) : phase === TrafficLightPhase.CandidateOrder ? (
          <PrimaryButton
            onClick={() => setPhase(TrafficLightPhase.CriteriaSetup)}
            text={intl.formatMessage({ id: 'trafficLightModal.next.button' })}
          />
        ) : phase === TrafficLightPhase.CriteriaSetup ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <PrimaryButton
              disabled={!allCriteriaFilled}
              onClick={() => {
                if (false) {
                  //remove in future
                  if (isDescriptionEnabled && !isConfirmingGeneration) {
                    setIsConfirmingGeneration(true);
                  } else {
                    handleGenerateDescriptions();
                  }
                }
                handleGenerateDescriptions();
              }}
            >
              {isGeneratingDescriptions ? (
                <Spinner size={SpinnerSize.small} styles={{ root: { marginRight: '0.5rem' } }} />
              ) : (
                <span style={{ marginRight: '0.5rem' }}>✨</span>
              )}
              <span>
                {isGeneratingDescriptions
                  ? intl.formatMessage({ id: 'trafficLightModal.generateDescriptions.loading' })
                  : isConfirmingGeneration
                    ? intl.formatMessage({ id: 'trafficLightModal.generateDescriptions.confirm' })
                    : intl.formatMessage({ id: 'trafficLightModal.generateDescriptions.button' })}
              </span>
            </PrimaryButton>

            <PrimaryButton
              onClick={() => setPhase(TrafficLightPhase.Ratings)}
              text="Next"
              disabled={!allCriteriaFilled || !isDescriptionEnabled}
            />
          </div>
        ) : (
          <>
            {orderedCheckedItems.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <DefaultButton
                  onClick={() => setCurrentItemIndex(currentItemIndex - 1)}
                  disabled={currentItemIndex === 0}
                  text={intl.formatMessage({ id: 'trafficLightModal.previous.button' })}
                />
                <Text>
                  {currentItemIndex + 1} of {orderedCheckedItems.length}
                </Text>
                <DefaultButton
                  onClick={() => setCurrentItemIndex(currentItemIndex + 1)}
                  disabled={currentItemIndex === orderedCheckedItems.length - 1}
                  text={intl.formatMessage({ id: 'trafficLightModal.next.button' })}
                />
              </div>
            )}
            <PrimaryButton
              onClick={handleSubmit}
              text={
                isSubmitting
                  ? intl.formatMessage({ id: 'trafficLightModal.submitting.button' })
                  : intl.formatMessage({ id: 'trafficLightModal.submit.button' })
              }
              disabled={!areAllCandidatesRated() || isSubmitting}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

export default TrafficLightModal;
